import { useEffect, useCallback } from 'react';
import gql from 'graphql-tag';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import {
	useAllCommentsData,
	buildDefaultCommentsContentStatusFilters,
	type Comment,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData';
import { useEditorAnnotations } from '@confluence/inline-comments-hooks/entry-points/useEditorAnnotations';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { useRendererActions } from '@confluence/renderer-actions/entry-points/RendererActionsContext';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { CONTENT_STATUS_ARCHIVED } from '@confluence/archive-pages/entry-points/archivingConstants';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { containsNetworkError } from '@confluence/network';
import { setContentErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';

import type {
	UnreadCommentCountUnreadCommentQuery as UnreadCommentCountUnreadCommentQuery$data,
	UnreadCommentCountUnreadCommentQueryVariables as UnreadCommentCountUnreadCommentQuery$variables,
	UnreadCommentCountUnreadCommentQuery_comments_PaginatedCommentList_nodes_Comment,
} from './__types__/UnreadCommentCountUnreadCommentQuery';
import { useUnreadComments } from './hooks/useUnreadComments';
import { useViewedComments } from './hooks/useViewedComments';
import { useUnreadCommentsObserverSetup } from './hooks/useUnreadCommentsObserverSetup';
import {
	useIsDanglingCallback,
	useDanglingAndResolvedHandler,
	type AnnotationStatus,
} from './hooks/useDanglingAndResolvedHandler';
import { GraphQLContentStatus } from './__types__/UnreadCommentQuery';
import { createUnreadCommentsSet } from './utils/unreadCommentsUtils';

const MAX_UNREAD_COMMENTS_SUPPORTED = 1000;

const unreadCommentsQuery = gql`
	query useUnreadCommentsSetupQuery($pageId: ID!, $contentStatus: [GraphQLContentStatus]) {
		comments(pageId: $pageId, contentStatus: $contentStatus, type: UNRESOLVED, depth: ROOT) {
			nodes {
				id
				location {
					type
					... on InlineComment {
						inlineMarkerRef
						inlineResolveProperties {
							resolved
							isDangling
						}
					}
				}
				author {
					permissionType
					... on KnownUser {
						accountId
					}
				}
				createdAt {
					value
				}
				replies {
					id
					author {
						permissionType
						... on KnownUser {
							accountId
						}
					}
					createdAt {
						value
					}
					location {
						type
						... on InlineComment {
							inlineMarkerRef
							inlineResolveProperties {
								resolved
							}
						}
					}
				}
			}
			totalCount
		}
	}
`;

export interface PageUnreadCommentsProps {
	pageMode: 'view' | 'edit';
	orderedActiveAnnotationIdList: AnnotationStatus[];
	commentsDataMap: { inline: Record<string, any>; general: Record<string, any> };
	addNewCommentThreads?: (commentsDataMap: {
		inline: Record<string, any>;
		general: Record<string, any>;
	}) => void;
}

export interface UseUnreadCommentsSetupProps {
	pageId?: string;
	pageContentTypeProps?: PageUnreadCommentsProps;
}

export const useUnreadCommentsSetup = ({
	pageId,
	pageContentTypeProps,
}: UseUnreadCommentsSetupProps) => {
	const [
		{ shouldUpdateUnreadCommentsOnFirstRender },
		{
			updateUnreadCommentsOnFirstRender,
			setIsUnreadQueryLoading,
			updateUnreadCommentIDSet,
			setShouldClearVisibleThreads,
		},
	] = useUnreadComments();

	const { userId } = useSessionData();

	// Setup observer for unread comments
	useUnreadCommentsObserverSetup({ pageId });

	// Destructure pageContentTypeProps with defaults
	const {
		pageMode,
		orderedActiveAnnotationIdList = [],
		commentsDataMap: legacyCommentsDataMap = { inline: {}, general: {} },
		addNewCommentThreads,
	} = pageContentTypeProps || {};

	const { contentStatus } = useContentStatus();

	// New comments data manager - choose which commentsDataMap to use
	const newCommentsDataResult = useAllCommentsData({
		contentId: pageId || '',
		contentStatus: buildDefaultCommentsContentStatusFilters(
			pageMode === 'view' ? PageMode.VIEW : PageMode.EDIT,
		),
		options: {
			skip: !expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) || !pageId,
		},
	});

	const commentsDataMap = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? newCommentsDataResult.commentsDataMap
		: legacyCommentsDataMap;

	const { isDanglingCallback } = useIsDanglingCallback({ pageMode });

	// We still need these for the logic in handleUpdateUnreadComments
	const [editorAnnotationsState] = useEditorAnnotations();
	const { annotations, pageId: editorPageId } = editorAnnotationsState;

	const isRenderer = pageMode === 'view';
	const rendererActionsResult = useRendererActions();
	const { rendererActions, pageId: rendererPageId } = rendererActionsResult || {};

	const { getViewedCommentsFresh } = useViewedComments({
		pageId: pageId || '',
	});

	const legacyQueryResult = useQueryInterop<
		UnreadCommentCountUnreadCommentQuery$data,
		UnreadCommentCountUnreadCommentQuery$variables
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
	>(unreadCommentsQuery, {
		variables: {
			pageId: pageId || '',
			contentStatus: !isRenderer
				? [GraphQLContentStatus.CURRENT, GraphQLContentStatus.DRAFT]
				: [GraphQLContentStatus.CURRENT],
		},
		skip:
			!pageId ||
			!fg('confluence_frontend_unread_comments_fixes') ||
			expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
	});

	const loading = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? newCommentsDataResult.loading || !newCommentsDataResult.isDataProcessed
		: legacyQueryResult.loading;
	const error = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? newCommentsDataResult.error
		: legacyQueryResult.error;

	useEffect(() => {
		if (!fg('confluence_frontend_unread_comments_fixes')) {
			return;
		}

		// isUnreadQueryLoading is used in other areas of the code to make sure unread logic is processed before further logic
		setIsUnreadQueryLoading(loading);
	}, [loading, setIsUnreadQueryLoading]);

	// Handle GraphQL query errors with proper error handling
	useEffect(() => {
		if (!fg('confluence_frontend_unread_comments_fixes') || !error || !pageId) {
			return;
		}

		// Handle expected errors (403/unauthorized, network errors) - just mark as handled
		if (isUnauthorizedError(error) || containsNetworkError(error)) {
			markErrorAsHandled(error);
			return;
		}

		// Handle unexpected errors (including 500) - track them with context
		setContentErrorAttributes(error, {
			objectId: pageId,
			objectType: 'page',
		});

		getMonitoringClient().submitError(error, {
			attribution: Attribution.COMMENTS,
		});

		markErrorAsHandled(error);
	}, [error, pageId]);

	useEffect(() => {
		if (!fg('confluence_frontend_unread_comments_fixes')) {
			return;
		}

		if (pageId) {
			updateUnreadCommentIDSet(new Set(), 'removeAll');
			setShouldClearVisibleThreads(false);
			// reset so we can recreate the set for the page that the user is changing to
			updateUnreadCommentsOnFirstRender(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageId]);

	const resetStates = useCallback(() => {
		updateUnreadCommentsOnFirstRender(false);
	}, [updateUnreadCommentsOnFirstRender]);

	const handleUpdateUnreadComments = useCallback(async () => {
		if (userId && shouldUpdateUnreadCommentsOnFirstRender) {
			if (loading) {
				return [];
			}

			if (!pageId) {
				return;
			}

			const currentAllCommentsFromQuery: (
				| UnreadCommentCountUnreadCommentQuery_comments_PaginatedCommentList_nodes_Comment
				| Comment
				| null
			)[] = (() => {
				// When feature flag is enabled, use data from useAllCommentsData
				if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
					return newCommentsDataResult.openComments || [];
				}

				// Legacy path: use data from unreadCommentsQuery
				// Validate that the query was executed for the current page
				if (legacyQueryResult.variables?.pageId !== pageId) {
					return [];
				}

				const filteredNodes =
					(legacyQueryResult.data?.comments?.nodes?.filter(
						(node) => node !== null,
					) as UnreadCommentCountUnreadCommentQuery_comments_PaginatedCommentList_nodes_Comment[]) ||
					[];

				if (filteredNodes.length === 0) {
					return [];
				}

				return filteredNodes;
			})();

			if (currentAllCommentsFromQuery.length === 0) {
				resetStates(); // we no longer need to create the set if there are no comments on the page
				return;
			}

			// Skip if renderer actions or editor annotations are for different page
			if (
				pageMode &&
				((isRenderer && rendererActions && rendererPageId !== pageId) ||
					(!isRenderer && annotations && editorPageId !== pageId))
			) {
				return; // Wait for next rerender when rendererActions / annotations are updated for the current page
			}

			const rendererAnnotations = rendererActions?.getAnnotationMarks() || [];
			const annotationsInEditorDoc = new Set<string>(annotations || []);

			if (currentAllCommentsFromQuery.length < MAX_UNREAD_COMMENTS_SUPPORTED) {
				const isArchived = contentStatus === CONTENT_STATUS_ARCHIVED;

				const hasPageComments = currentAllCommentsFromQuery.some(
					(
						comment:
							| UnreadCommentCountUnreadCommentQuery_comments_PaginatedCommentList_nodes_Comment
							| Comment
							| null,
					) => comment?.location?.type === 'FOOTER',
				);

				// skip creating unread comments set if the page is archived
				// or if there are no page comments and inline comments
				if (
					isArchived ||
					(pageMode &&
						!hasPageComments &&
						((isRenderer && rendererAnnotations?.length === 0) ||
							(!isRenderer && annotationsInEditorDoc?.size === 0)))
				) {
					return;
				}

				// continuing here means one of these 3 scenarios are true so we do want to check for unread comments
				// 1. no inline comments, yes page comments
				// 2. yes inline comments, no page comments
				// 3. yes inline comments, yes page comments

				const viewedComments = await getViewedCommentsFresh();

				if (viewedComments.success && viewedComments.data) {
					void createUnreadCommentsSet({
						allComments: currentAllCommentsFromQuery,
						currentUserId: userId,
						pageId,
						updateUnreadCommentIDSet,
						resetStates,
						viewedComments: viewedComments.data,
						isDanglingCallback,
						addNewCommentThreads,
					});
				}
			}
		}
	}, [
		userId,
		shouldUpdateUnreadCommentsOnFirstRender,
		loading,
		pageId,
		pageMode,
		isRenderer,
		rendererActions,
		rendererPageId,
		annotations,
		editorPageId,
		legacyQueryResult.variables?.pageId,
		legacyQueryResult.data?.comments?.nodes,
		newCommentsDataResult.openComments,
		resetStates,
		contentStatus,
		getViewedCommentsFresh,
		updateUnreadCommentIDSet,
		isDanglingCallback,
		addNewCommentThreads,
	]);

	useDanglingAndResolvedHandler({
		orderedActiveAnnotationIdList,
		commentsDataMap,
		userId: userId || '',
		pageId: pageId || '',
		isDanglingCallback: pageMode ? isDanglingCallback : undefined, // use dangling callback if content type is a page
	});

	useEffect(() => {
		const isNotPageContentType = !pageContentTypeProps;
		if (
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			process.env.REACT_SSR ||
			(isNotPageContentType && !fg('cc-whiteboards-consolidating-comments-unread')) ||
			(pageContentTypeProps && !fg('confluence_frontend_unread_comments_fixes'))
		) {
			return;
		}

		void handleUpdateUnreadComments();
	}, [handleUpdateUnreadComments, rendererActions, annotations, pageContentTypeProps]);
};
